import { getNativeNfcPlugin, NfcReaderError } from './nfcReader';

const NATIVE_DIAGNOSTIC_CODES = new Set([
  'nfc.tag_type_unsupported',
  'nfc.apdu_build_failed',
  'nfc.transceive_failed',
  'nfc.get_version_status_invalid',
  'nfc.additional_frame_failed',
  'nfc.response_submit_failed',
  'nfc.backend_response_invalid',
  'nfc.session_invalidated',
  'nfc.tag_removed',
  'nfc.timeout',
]);
const NATIVE_DIAGNOSTIC_PHASES = new Set([
  'GET_VERSION_HW', 'GET_VERSION_SW', 'GET_VERSION_PRODUCTION',
  'ISO_SELECT_NDEF', 'KEY_VERSION_0', 'KEY_VERSION_1', 'KEY_VERSION_2',
  'FILE_SETTINGS', 'AUTH_FIRST_0', 'AUTH_CONTINUE_0', 'PROTECTED_VERSION_0',
  'PROTECTED_UID_0', 'AUTH_FIRST_1', 'AUTH_CONTINUE_1', 'PROTECTED_VERSION_1',
  'PROTECTED_UID_1', 'AUTH_FIRST_2', 'AUTH_CONTINUE_2', 'PROTECTED_VERSION_2',
  'PROTECTED_UID_2', 'EV2_READ_ONLY', 'PROVISIONING', 'PROVISIONING_WRITE', 'CORE_NFC',
]);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CORE_NFC_DIAGNOSTICS = Object.freeze({
  tag_connection_lost: { category: 'transport', numericCode: 100 },
  tag_not_connected: { category: 'transport', numericCode: 104 },
  retry_exceeded: { category: 'transport', numericCode: 101 },
  tag_response_error: { category: 'tag_response', numericCode: 102 },
  session_invalidated: { category: 'session', numericCode: 103 },
  packet_too_long: { category: 'command', numericCode: 105 },
  radio_disabled: { category: 'device', numericCode: 6 },
  session_timeout: { category: 'session', numericCode: 201 },
  system_busy: { category: 'session', numericCode: 203 },
  user_canceled: { category: 'user', numericCode: 200 },
  unclassified: { category: 'other', numericCode: null },
});
const NATIVE_DIAGNOSTIC_FIELDS = new Set([
  'schema', 'phase', 'code', 'sw1', 'sw2', 'coreNfcCode', 'coreNfcCategory',
  'coreNfcDomain', 'coreNfcNumericCode', 'sessionElapsedMs', 'exchangeElapsedMs',
  'responseLength', 'sessionId', 'correlationId',
]);

export function parseNativeNfcDiagnosticObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || Object.keys(value).some(key => !NATIVE_DIAGNOSTIC_FIELDS.has(key))
      || value.schema !== 2
      || !NATIVE_DIAGNOSTIC_PHASES.has(value.phase)
      || !NATIVE_DIAGNOSTIC_CODES.has(value.code)
      || !Number.isInteger(value.sessionElapsedMs)
      || value.sessionElapsedMs < 0 || value.sessionElapsedMs > 600000
      || typeof value.sessionId !== 'string' || !UUID_PATTERN.test(value.sessionId)
      || typeof value.correlationId !== 'string' || !UUID_PATTERN.test(value.correlationId)) {
    return null;
  }

  const diagnostic = {
    phase: value.phase,
    code: value.code,
    sessionElapsedMs: value.sessionElapsedMs,
    sessionId: value.sessionId.toLowerCase(),
    correlationId: value.correlationId.toLowerCase(),
  };

  const hasCoreNfcCode = value.coreNfcCode !== undefined;
  const hasCoreNfcCategory = value.coreNfcCategory !== undefined;
  if (hasCoreNfcCode !== hasCoreNfcCategory) return null;
  if (hasCoreNfcCode) {
    const metadata = CORE_NFC_DIAGNOSTICS[value.coreNfcCode];
    if (!metadata || metadata.category !== value.coreNfcCategory) return null;
    diagnostic.coreNfcCode = value.coreNfcCode;
    diagnostic.coreNfcCategory = value.coreNfcCategory;
  }

  const hasDomain = value.coreNfcDomain !== undefined;
  const hasNumericCode = value.coreNfcNumericCode !== undefined;
  if (hasDomain !== hasNumericCode) return null;
  if (hasDomain) {
    const metadata = CORE_NFC_DIAGNOSTICS[value.coreNfcCode];
    if (!hasCoreNfcCode || metadata.numericCode === null
        || value.coreNfcDomain !== 'NFCErrorDomain'
        || value.coreNfcNumericCode !== metadata.numericCode) return null;
    diagnostic.coreNfcDomain = value.coreNfcDomain;
    diagnostic.coreNfcNumericCode = value.coreNfcNumericCode;
  }

  const hasResponseLength = value.responseLength !== undefined;
  const hasSw1 = value.sw1 !== undefined;
  const hasSw2 = value.sw2 !== undefined;
  if (hasResponseLength !== hasSw1 || hasResponseLength !== hasSw2) return null;
  if (hasResponseLength) {
    if (!Number.isInteger(value.responseLength) || value.responseLength < 0
        || value.responseLength > 512 || !/^[0-9A-F]{2}$/i.test(value.sw1)
        || !/^[0-9A-F]{2}$/i.test(value.sw2)
        || value.exchangeElapsedMs === undefined) return null;
    diagnostic.responseLength = value.responseLength;
    diagnostic.sw1 = value.sw1.toUpperCase();
    diagnostic.sw2 = value.sw2.toUpperCase();
  }

  if (value.exchangeElapsedMs !== undefined) {
    if (!Number.isInteger(value.exchangeElapsedMs)
        || value.exchangeElapsedMs < 0 || value.exchangeElapsedMs > 15000) return null;
    diagnostic.exchangeElapsedMs = value.exchangeElapsedMs;
  }
  return diagnostic;
}

export function parseNativeNfcDiagnostic(error) {
  const message = typeof error?.message === 'string' ? error.message : '';
  const match = message.match(
    /NFC_DIAGNOSTIC\|schema=2\|phase=([A-Z0-9_]+)\|code=([a-z_.]+)\|sw1=(--|[0-9A-F]{2})\|sw2=(--|[0-9A-F]{2})(?:\|core_nfc_code=([a-z_]+)\|core_nfc_category=([a-z_]+))?(?:\|core_nfc_domain=(NFCErrorDomain)\|core_nfc_numeric_code=(\d{1,3}))?\|session_elapsed_ms=(\d{1,6})(?:\|exchange_elapsed_ms=(\d{1,5}))?(?:\|response_length=(\d{1,3}))?\|session_id=([0-9a-f-]{36})\|correlation_id=([0-9a-f-]{36})$/i,
  );
  if (!match) return null;
  const [
    ,
    phase,
    code,
    sw1,
    sw2,
    coreNfcCode,
    coreNfcCategory,
    coreNfcDomain,
    coreNfcNumericCode,
    sessionElapsedMs,
    exchangeElapsedMs,
    responseLength,
    sessionId,
    correlationId,
  ] = match;
  const coreNfcMetadata = coreNfcCode ? CORE_NFC_DIAGNOSTICS[coreNfcCode] : null;
  const sessionElapsed = Number(sessionElapsedMs);
  const exchangeElapsed = exchangeElapsedMs === undefined ? null : Number(exchangeElapsedMs);
  const responseBytes = responseLength === undefined ? null : Number(responseLength);
  if (!NATIVE_DIAGNOSTIC_PHASES.has(phase)
      || !NATIVE_DIAGNOSTIC_CODES.has(code)
      || !UUID_PATTERN.test(sessionId)
      || !UUID_PATTERN.test(correlationId)) return null;
  if (sessionElapsed > 600000
      || (exchangeElapsed !== null && exchangeElapsed > 15000)
      || (responseBytes !== null && responseBytes > 512)) return null;
  if (coreNfcCode) {
    if (!coreNfcMetadata || coreNfcMetadata.category !== coreNfcCategory || exchangeElapsed === null) {
      return null;
    }
    if (coreNfcMetadata.numericCode === null) {
      if (coreNfcDomain !== undefined || coreNfcNumericCode !== undefined) return null;
    } else if ((coreNfcDomain !== undefined || coreNfcNumericCode !== undefined)
        && (coreNfcDomain !== 'NFCErrorDomain'
          || Number(coreNfcNumericCode) !== coreNfcMetadata.numericCode)) return null;
  } else if (coreNfcDomain !== undefined || coreNfcNumericCode !== undefined) {
    return null;
  }
  if (responseBytes === null) {
    if (sw1 !== '--' || sw2 !== '--') return null;
  } else if (sw1 === '--' || sw2 === '--' || exchangeElapsed === null) {
    return null;
  }
  return {
    phase,
    code,
    sw1: responseBytes === null ? null : sw1.toUpperCase(),
    sw2: responseBytes === null ? null : sw2.toUpperCase(),
    ...(coreNfcCode ? {
      coreNfcCode,
      coreNfcCategory,
      ...(coreNfcDomain ? { coreNfcDomain, coreNfcNumericCode: Number(coreNfcNumericCode) } : {}),
    } : {}),
    sessionElapsedMs: sessionElapsed,
    ...(exchangeElapsed !== null ? { exchangeElapsedMs: exchangeElapsed } : {}),
    ...(responseBytes !== null ? { responseLength: responseBytes } : {}),
    sessionId: sessionId.toLowerCase(),
    correlationId: correlationId.toLowerCase(),
  };
}

function nativeDiagnosticError(error, fallbackCode, fallbackMessage) {
  if (error?.code === 'RECOVERY_REQUIRED') {
    return new NfcReaderError(
      'RECOVERY_REQUIRED',
      'Provisioning interrotto; recupero richiesto. Non ripetere la scansione.',
    );
  }
  const diagnostic = parseNativeNfcDiagnostic(error);
  if (diagnostic) {
    const safeError = new NfcReaderError(
      'NFC_READ_INTERRUPTED',
      'Lettura NFC interrotta — tag non modificato',
    );
    safeError.diagnostic = diagnostic;
    return safeError;
  }
  return new NfcReaderError(fallbackCode, fallbackMessage);
}

// No Web implementation, endpoint URL, signing primitive or APDU transport.
async function command(method, options) {
  const root = typeof window !== 'undefined' ? window : globalThis;
  if (root.Capacitor?.isNativePlatform?.() !== true
      || root.Capacitor?.getPlatform?.() !== 'ios') {
    throw new NfcReaderError('NFC_ATTESTATION_UNAVAILABLE', 'Canale NFC iOS non disponibile');
  }
  const plugin = getNativeNfcPlugin();
  if (!plugin || typeof plugin[method] !== 'function'
      || !options || typeof options.accessToken !== 'string'
      || Object.keys(options).some(key => !['accessToken', 'tenantContext'].includes(key))) {
    throw new NfcReaderError('NFC_ATTESTATION_FAILED', 'Canale NFC non verificato');
  }
  let result;
  try {
    result = await plugin[method](options);
  } catch {
    // Never propagate native response/error objects into JS diagnostics.
    throw new NfcReaderError('NFC_ATTESTATION_FAILED', 'Canale NFC non verificato');
  }
  if (result?.verified !== true || result?.writer_enabled !== false
      || typeof result?.revoked !== 'boolean'
      || Object.keys(result).some(key => !['verified', 'revoked', 'writer_enabled'].includes(key))) {
    throw new NfcReaderError('NFC_ATTESTATION_FAILED', 'Risultato nativo non valido');
  }
  return { verified: true, revoked: result.revoked, writer_enabled: false };
}

export const prepareNativeNfcChannel = options => command('prepareNfcChannel', options);
export const revokeNativeNfcInstance = options => command('revokeNfcInstance', options);

export async function submitNativeNfcAttendance(options) {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const plugin = getNativeNfcPlugin();
  if (root.Capacitor?.isNativePlatform?.() !== true
      || root.Capacitor?.getPlatform?.() !== 'ios'
      || !plugin || typeof plugin.runNativeAttendance !== 'function'
      || !options || typeof options.accessToken !== 'string'
      || Object.keys(options).some(key => !['accessToken', 'tenantContext'].includes(key))) {
    throw new NfcReaderError('NFC_ATTESTATION_UNAVAILABLE', 'Timbratura NFC nativa non disponibile');
  }
  let result;
  try {
    result = await plugin.runNativeAttendance(options);
  } catch {
    throw new NfcReaderError('NFC_ATTESTATION_FAILED', 'Timbratura NFC non verificata');
  }
  const allowedOutcomes = ['OPENED', 'CLOSED', 'DA_VALIDARE', 'IDEMPOTENT'];
  const allowedEvents = ['ENTRY', 'EXIT', 'UNKNOWN'];
  if (!result || !allowedOutcomes.includes(result.outcome)
      || !allowedEvents.includes(result.eventType)
      || result.writerEnabled !== false
      || Object.keys(result).some(key => !['outcome', 'eventType', 'writerEnabled'].includes(key))) {
    throw new NfcReaderError('NFC_ATTESTATION_FAILED', 'Risultato NFC non valido');
  }
  return { outcome: result.outcome, eventType: result.eventType };
}

export async function provisionNativeNfcTag(options) {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const plugin = getNativeNfcPlugin();
  if (root.Capacitor?.isNativePlatform?.() !== true
      || root.Capacitor?.getPlatform?.() !== 'ios'
      || !plugin || typeof plugin.runNativeProvisioning !== 'function'
      || !options || typeof options.accessToken !== 'string'
      || typeof options.pointId !== 'string'
      || !/^[0-9a-f-]{36}$/i.test(options.pointId)
      || Object.keys(options).some(key => !['accessToken', 'tenantContext', 'pointId'].includes(key))) {
    throw new NfcReaderError('NFC_PROVISIONING_UNAVAILABLE', 'Provisioning NFC disponibile solo su iPhone');
  }
  let result;
  try {
    result = await plugin.runNativeProvisioning(options);
  } catch (error) {
    throw nativeDiagnosticError(
      error,
      'NFC_PROVISIONING_FAILED',
      'Associazione NFC non confermata; contatta l’amministratore prima di riprovare',
    );
  }
  if (!result || result.phase !== 'COMPLETED' || result.associated !== true
      || result.rf_closed !== true
      || Object.keys(result).some(key => !['phase', 'associated', 'rf_closed'].includes(key))) {
    throw new NfcReaderError('NFC_PROVISIONING_FAILED', 'Risultato di associazione NFC non valido');
  }
  return { associated: true };
}

export async function verifyNativeReadOnlyEV2(options) {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const plugin = getNativeNfcPlugin();
  if (root.Capacitor?.isNativePlatform?.() !== true
      || root.Capacitor?.getPlatform?.() !== 'ios'
      || !plugin || typeof plugin.runAttestedReadOnlyEV2 !== 'function'
      || !options || typeof options.accessToken !== 'string'
      || typeof options.pointId !== 'string' || !UUID_PATTERN.test(options.pointId)
      || Object.keys(options).some(key => !['accessToken', 'tenantContext', 'pointId'].includes(key))) {
    throw new NfcReaderError('NFC_EV2_UNAVAILABLE', 'Verifica EV2 nativa non disponibile');
  }
  try {
    const result = await plugin.runAttestedReadOnlyEV2(options);
    if (result?.outcome === 'READ_ONLY_DIAGNOSTIC') {
      const envelopeKeys = Object.keys(result);
      if (result.writer_enabled !== false
          || envelopeKeys.some(key => !['outcome', 'writer_enabled', 'diagnostic'].includes(key))
          || envelopeKeys.length !== 3) {
        throw new NfcReaderError('NFC_EV2_INVALID_RESULT', 'Risultato EV2 non valido');
      }
      const diagnostic = parseNativeNfcDiagnosticObject(result.diagnostic);
      if (!diagnostic) {
        throw new NfcReaderError('NFC_EV2_INVALID_RESULT', 'Risultato EV2 non valido');
      }
      const safeError = new NfcReaderError(
        'NFC_READ_INTERRUPTED',
        'Lettura NFC interrotta — tag non modificato',
      );
      safeError.diagnostic = diagnostic;
      throw safeError;
    }
    if (!result || result.phase !== 'INITIAL_STATE_VERIFIED'
        || result.authenticated !== true || result.read_only !== true
        || result.writer_enabled !== false
        || Object.keys(result).some(key => !['phase', 'authenticated', 'read_only', 'writer_enabled'].includes(key))) {
      throw new NfcReaderError('NFC_EV2_INVALID_RESULT', 'Risultato EV2 non valido');
    }
    return { authenticated: true, readOnly: true, writerEnabled: false };
  } catch (error) {
    if (error instanceof NfcReaderError) throw error;
    throw nativeDiagnosticError(
      error,
      'NFC_EV2_FAILED',
      'Verifica EV2 non riuscita; non ripetere la scansione senza aver letto la diagnosi',
    );
  }
}
